import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, ToggleLeft, ToggleRight, X, Check, Timer } from "lucide-react";
import { toast } from "sonner";
import {
  getRegrasAtraso,
  createRegraAtraso,
  updateRegraAtraso,
  deleteRegraAtraso,
  formatCNPJ,
} from "@/lib/ocorenApi";

const inputClass =
  "w-full px-3 py-2 text-sm border border-[#E4E4E7] rounded-md focus:outline-none focus:border-[#09090B] bg-white text-[#09090B]";
const labelClass =
  "block text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-semibold mb-1.5";

const RegraForm = ({ onCancel, onSaved }) => {
  const [cnpj, setCnpj] = useState("");
  const [prazo, setPrazo] = useState(5);
  const [descricao, setDescricao] = useState("");
  const [priority, setPriority] = useState(50);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (prazo === "" || Number(prazo) < 0) {
      toast.error("Informe um prazo válido (dias)");
      return;
    }
    setSaving(true);
    try {
      await createRegraAtraso({
        cnpj_cliente: cnpj.replace(/\D/g, "") || null,
        prazo_dias: Number(prazo),
        descricao: descricao.trim(),
        priority: Number(priority) || 50,
        active: true,
      });
      toast.success("Regra de prazo criada");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="regra-atraso-form"
      className="bg-white border border-[#09090B] rounded-md p-5 space-y-4 mb-6"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-semibold text-[#09090B]">
          Nova regra de prazo (SLA)
        </h3>
        <button onClick={onCancel} className="text-[#52525B] hover:text-[#09090B]">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>CNPJ do cliente (destino)</label>
          <input
            data-testid="regra-atraso-cnpj"
            value={cnpj}
            onChange={(e) => setCnpj(e.target.value)}
            placeholder="Em branco = todos"
            className={`${inputClass} font-mono-data`}
          />
        </div>
        <div>
          <label className={labelClass}>Prazo (dias)</label>
          <input
            data-testid="regra-atraso-prazo"
            type="number"
            min="0"
            value={prazo}
            onChange={(e) => setPrazo(e.target.value)}
            className={`${inputClass} font-mono-data`}
          />
        </div>
        <div>
          <label className={labelClass}>Prioridade</label>
          <input
            data-testid="regra-atraso-priority"
            type="number"
            min="0"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className={`${inputClass} font-mono-data`}
          />
        </div>
      </div>
      <div>
        <label className={labelClass}>Descrição (opcional)</label>
        <input
          data-testid="regra-atraso-descricao"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="Ex: SLA acordado com cliente X — 3 dias"
          className={inputClass}
        />
      </div>
      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E4E4E7]">
        <button
          onClick={onCancel}
          className="bg-white border border-[#E4E4E7] hover:border-[#09090B] text-[#09090B] px-4 py-2 text-sm font-medium rounded-md"
        >
          Cancelar
        </button>
        <button
          data-testid="regra-atraso-save"
          onClick={save}
          disabled={saving}
          className="bg-[#09090B] text-white hover:bg-[#27272A] disabled:opacity-60 px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2"
        >
          <Check size={14} />
          {saving ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
};

export const RegrasAtraso = () => {
  const [regras, setRegras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getRegrasAtraso();
      setRegras(r.items || []);
    } catch {
      toast.error("Falha ao carregar regras de prazo");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onToggle = async (r) => {
    try {
      await updateRegraAtraso(r.id, { active: !r.active });
      refresh();
    } catch {
      toast.error("Falha ao atualizar");
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm("Remover esta regra de prazo?")) return;
    try {
      await deleteRegraAtraso(r.id);
      toast.success("Regra removida");
      refresh();
    } catch {
      toast.error("Falha ao remover");
    }
  };

  return (
    <div data-testid="regras-atraso-wrapper">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-[#52525B] max-w-2xl">
          Defina prazos de entrega por CNPJ de cliente. Quando não houver regra
          nem data prevista cadastrada, o padrão é <strong>5 dias</strong> a partir
          do primeiro movimento. (Regras por localidade chegam na fase de CTE.)
        </div>
        <button
          data-testid="regra-atraso-create-btn"
          onClick={() => setCreating(true)}
          className="bg-[#09090B] text-white hover:bg-[#27272A] px-4 py-2 text-sm font-semibold rounded-md flex items-center gap-2 shrink-0"
        >
          <Plus size={15} />
          Nova regra
        </button>
      </div>

      {creating && (
        <RegraForm
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}

      <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
        <table className="data-table w-full text-sm" data-testid="regras-atraso-table">
          <thead>
            <tr>
              <th>CNPJ Cliente</th>
              <th className="text-right">Prazo (dias)</th>
              <th className="text-right">Prioridade</th>
              <th>Descrição</th>
              <th className="text-center">Ativa</th>
              <th className="w-16 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="text-center text-[#52525B] py-10">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && regras.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-[#52525B] py-10">
                  Nenhuma regra de prazo. O padrão de 5 dias está em uso.
                </td>
              </tr>
            )}
            {!loading &&
              regras.map((r, idx) => (
                <tr key={r.id} data-testid={`regra-atraso-row-${idx}`}>
                  <td className="font-mono-data text-xs">
                    {r.cnpj_cliente ? (
                      formatCNPJ(r.cnpj_cliente)
                    ) : (
                      <span className="text-[#A1A1AA]">Todos os clientes</span>
                    )}
                  </td>
                  <td className="text-right font-mono-data font-semibold">
                    <span className="inline-flex items-center gap-1">
                      <Timer size={12} className="text-[#52525B]" />
                      {r.prazo_dias}
                    </span>
                  </td>
                  <td className="text-right font-mono-data text-xs">{r.priority}</td>
                  <td className="text-xs text-[#52525B] max-w-md truncate">
                    {r.descricao || "—"}
                  </td>
                  <td className="text-center">
                    <button
                      data-testid={`regra-atraso-toggle-${idx}`}
                      onClick={() => onToggle(r)}
                      className="text-[#09090B] hover:opacity-70"
                    >
                      {r.active ? (
                        <ToggleRight size={20} className="text-emerald-600" />
                      ) : (
                        <ToggleLeft size={20} className="text-[#A1A1AA]" />
                      )}
                    </button>
                  </td>
                  <td>
                    <div className="flex items-center justify-end">
                      <button
                        data-testid={`regra-atraso-delete-${idx}`}
                        onClick={() => onDelete(r)}
                        className="p-1.5 hover:bg-[#FAFAFA] rounded-sm text-[#52525B] hover:text-[#F43F5E]"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RegrasAtraso;
